import { useState, useCallback, useMemo, type ReactNode } from "react";
import { cn, Skeleton, Checkbox, Button } from "@cieda/djayma-ui";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ColumnDef<T = unknown> {
  readonly id: string;
  readonly header: string | (() => ReactNode);
  readonly accessorKey?: string;
  readonly cell?: (row: T) => ReactNode;
  readonly sortable?: boolean;
  /** Visible on mobile card view? Defaults to true for first 3 columns. */
  readonly mobileVisible?: boolean;
}

export interface PaginationState {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
}

export interface SortState {
  readonly column: string;
  readonly direction: "asc" | "desc";
}

export interface AdminDataTableProps<T> {
  readonly columns: readonly ColumnDef<T>[];
  readonly data: readonly T[];
  readonly pagination?: PaginationState;
  readonly onPageChange?: (page: number) => void;
  readonly onSort?: (sort: SortState) => void;
  readonly onRowSelect?: (selectedIds: readonly string[]) => void;
  readonly isLoading?: boolean;
  readonly emptyMessage?: string;
  /** Extract unique id from row, defaults to (row as any).id */
  readonly getRowId?: (row: T) => string;
  /** Optional controlled sort — when provided, overrides internal sort state. */
  readonly currentSort?: SortState | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((acc: unknown, key) => {
    if (acc != null && typeof acc === "object") {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function defaultGetRowId<T>(row: T): string {
  return String((row as Record<string, unknown>).id ?? "");
}

// ---------------------------------------------------------------------------
// Table header cell
// ---------------------------------------------------------------------------

function SortableHeader({
  column,
  currentSort,
  onSort,
}: {
  column: ColumnDef<unknown>;
  currentSort: SortState | null;
  onSort?: (sort: SortState) => void;
}) {
  const isActive = currentSort?.column === column.id;
  const direction = isActive ? currentSort!.direction : null;

  const handleClick = () => {
    if (!column.sortable || !onSort) return;
    const nextDir = direction === "asc" ? "desc" : "asc";
    onSort({ column: column.id, direction: nextDir });
  };

  return (
    <th
      className={cn(
        "whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500",
        column.sortable && "cursor-pointer select-none hover:text-stone-700",
      )}
      onClick={column.sortable ? handleClick : undefined}
    >
      <span className="inline-flex items-center gap-1">
        {typeof column.header === "function" ? column.header() : column.header}
        {column.sortable && (
          <span className="inline-flex flex-col">
            <ChevronUp
              className={cn(
                "h-3 w-3 -mb-0.5",
                direction === "asc" ? "text-amber-600" : "text-stone-300",
              )}
            />
            <ChevronDown
              className={cn(
                "h-3 w-3 -mt-0.5",
                direction === "desc" ? "text-amber-600" : "text-stone-300",
              )}
            />
          </span>
        )}
      </span>
    </th>
  );
}

// ---------------------------------------------------------------------------
// Mobile card view
// ---------------------------------------------------------------------------

function MobileCardView<T>({
  columns,
  data,
  selectedIds,
  onToggleRow,
  selectable,
  getRowId,
}: {
  columns: readonly ColumnDef<T>[];
  data: readonly T[];
  selectedIds: ReadonlySet<string>;
  onToggleRow: (id: string) => void;
  selectable: boolean;
  getRowId: (row: T) => string;
}) {
  // Show up to 4 columns on mobile cards
  const visibleColumns = columns.filter(
    (col, i) => col.mobileVisible !== false && i < 4,
  );

  return (
    <div className="space-y-3 md:hidden">
      {data.map((row) => {
        const rowId = getRowId(row);
        const isSelected = selectedIds.has(rowId);

        return (
          <div
            key={rowId}
            className={cn(
              "rounded-lg border bg-white p-4 shadow-sm",
              isSelected ? "border-amber-300 bg-amber-50/30" : "border-stone-200",
            )}
          >
            {selectable && (
              <div className="mb-2">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => onToggleRow(rowId)}
                  aria-label={`Sélectionner la ligne ${rowId}`}
                />
              </div>
            )}
            <dl className="space-y-1.5">
              {visibleColumns.map((col) => {
                const value = col.cell
                  ? col.cell(row)
                  : col.accessorKey
                    ? String(getNestedValue(row, col.accessorKey) ?? "")
                    : "";

                return (
                  <div key={col.id} className="flex items-baseline justify-between gap-2">
                    <dt className="text-xs font-medium text-stone-500">{typeof col.header === "function" ? col.header() : col.header}</dt>
                    <dd className="text-sm text-stone-900">{value}</dd>
                  </div>
                );
              })}
            </dl>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function TableSkeleton({ columns, rows = 5 }: { columns: number; rows?: number }) {
  return (
    <>
      {/* Desktop skeleton */}
      <div className="hidden md:block">
        <div className="overflow-hidden rounded-lg border border-stone-200">
          <table className="w-full">
            <thead className="bg-stone-50">
              <tr>
                {Array.from({ length: columns }).map((_, i) => (
                  <th key={i} className="px-4 py-3">
                    <Skeleton className="h-4 w-20" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }).map((_, r) => (
                <tr key={r} className="border-t border-stone-100">
                  {Array.from({ length: columns }).map((_, c) => (
                    <td key={c} className="px-4 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile skeleton */}
      <div className="space-y-3 md:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="rounded-lg border border-stone-200 p-4">
            <Skeleton className="mb-2 h-4 w-32" />
            <Skeleton className="mb-1 h-3 w-24" />
            <Skeleton className="h-3 w-20" />
          </div>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Pagination controls
// ---------------------------------------------------------------------------

function PaginationControls({
  pagination,
  onPageChange,
}: {
  pagination: PaginationState;
  onPageChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
  const canPrev = pagination.page > 1;
  const canNext = pagination.page < totalPages;
  const start = (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="flex items-center justify-between px-1 py-3">
      <p className="text-xs text-stone-500">
        {start}-{end} sur {new Intl.NumberFormat("fr-SN").format(pagination.total)}
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={!canPrev}
          onClick={() => onPageChange(pagination.page - 1)}
          className="gap-1"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Précédent</span>
        </Button>

        <span className="px-2 text-xs text-stone-500">
          {pagination.page}/{totalPages}
        </span>

        <Button
          variant="outline"
          size="sm"
          disabled={!canNext}
          onClick={() => onPageChange(pagination.page + 1)}
          className="gap-1"
        >
          <span className="hidden sm:inline">Suivant</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AdminDataTable<T>({
  columns,
  data,
  pagination,
  onPageChange,
  onSort,
  onRowSelect,
  isLoading,
  emptyMessage = "Aucune donnée disponible.",
  getRowId = defaultGetRowId,
  currentSort,
}: AdminDataTableProps<T>) {
  const [internalSort, setInternalSort] = useState<SortState | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());

  const selectable = !!onRowSelect;

  // Controlled if parent passed currentSort; otherwise use internal state.
  const sort = currentSort !== undefined ? currentSort : internalSort;

  const handleSort = useCallback(
    (nextSort: SortState) => {
      if (currentSort === undefined) {
        setInternalSort(nextSort);
      }
      onSort?.(nextSort);
    },
    [onSort, currentSort],
  );

  const toggleRow = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        onRowSelect?.(Array.from(next));
        return next;
      });
    },
    [onRowSelect],
  );

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allIds = data.map(getRowId);
      const allSelected = allIds.every((id) => prev.has(id));
      const next = allSelected ? new Set<string>() : new Set(allIds);
      onRowSelect?.(Array.from(next));
      return next;
    });
  }, [data, getRowId, onRowSelect]);

  const allSelected = useMemo(() => {
    if (data.length === 0) return false;
    return data.every((row) => selectedIds.has(getRowId(row)));
  }, [data, selectedIds, getRowId]);

  // Loading state
  if (isLoading) {
    return <TableSkeleton columns={columns.length + (selectable ? 1 : 0)} />;
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white py-12 text-center">
        <p className="text-sm text-stone-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Desktop table */}
      <div className="hidden overflow-x-auto md:block">
        <div className="overflow-hidden rounded-lg border border-stone-200">
          <table className="w-full">
            <thead className="bg-stone-50">
              <tr>
                {selectable && (
                  <th className="w-10 px-4 py-3">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Tout sélectionner"
                    />
                  </th>
                )}
                {columns.map((col) => (
                  <SortableHeader
                    key={col.id}
                    column={col as ColumnDef<unknown>}
                    currentSort={sort}
                    onSort={handleSort}
                  />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {data.map((row) => {
                const rowId = getRowId(row);
                const isSelected = selectedIds.has(rowId);

                return (
                  <tr
                    key={rowId}
                    className={cn(
                      "transition-colors",
                      isSelected ? "bg-amber-50/40" : "hover:bg-stone-50",
                    )}
                  >
                    {selectable && (
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(rowId)}
                          aria-label={`Sélectionner ${rowId}`}
                        />
                      </td>
                    )}
                    {columns.map((col) => {
                      const value = col.cell
                        ? col.cell(row)
                        : col.accessorKey
                          ? String(getNestedValue(row, col.accessorKey) ?? "")
                          : "";

                      return (
                        <td key={col.id} className="px-4 py-3 text-sm text-stone-700">
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card view */}
      <MobileCardView
        columns={columns}
        data={data}
        selectedIds={selectedIds}
        onToggleRow={toggleRow}
        selectable={selectable}
        getRowId={getRowId}
      />

      {/* Pagination */}
      {pagination && onPageChange && (
        <PaginationControls pagination={pagination} onPageChange={onPageChange} />
      )}
    </div>
  );
}
